import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { Heart, Lock } from "lucide-react"

interface ProductCardProps {
  id: string
  name: string
  price: number
  image: string
  isApexLocked?: boolean
  isLimited?: boolean
  isWishlisted?: boolean
  onWishlist?: () => void
  onAddToCart?: () => void
  className?: string
}

export function ProductCard({
  name,
  price,
  image,
  isApexLocked = false,
  isLimited = false,
  isWishlisted = false,
  onWishlist,
  onAddToCart,
  className
}: ProductCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false)
  
  return (
    <Card className={cn(
      "group overflow-hidden border-0 bg-card-gradient",
      "hover:scale-[1.02] transition-all duration-300",
      className
    )}>
      <div className="relative aspect-[3/4] overflow-hidden">
        {/* Product Image */}
        <div className={cn(
          "w-full h-full bg-muted/20 transition-all duration-500",
          !imageLoaded && "animate-pulse"
        )}>
          <img
            src={image}
            alt={name}
            className={cn(
              "w-full h-full object-cover transition-all duration-700",
              "group-hover:scale-110",
              imageLoaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={() => setImageLoaded(true)}
          />
        </div>
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {isApexLocked && (
            <Badge className="bg-primary text-primary-foreground text-xs">
              <Lock size={10} className="mr-1" />
              Apex Only
            </Badge>
          )}
          {isLimited && (
            <Badge variant="destructive" className="text-xs animate-pulse-gold">
              Limited Drop
            </Badge>
          )}
        </div>
        
        {/* Wishlist Button */}
        <button
          onClick={onWishlist}
          className={cn(
            "absolute top-3 right-3 p-2 rounded-full",
            "bg-black/50 backdrop-blur-sm transition-all duration-200",
            "hover:bg-black/70 hover:scale-110",
            isWishlisted && "text-red-400"
          )}
        >
          <Heart 
            size={16} 
            className={cn(
              "transition-all duration-200",
              isWishlisted && "fill-current"
            )} 
          />
        </button>
        
        {/* Overlay on Hover */}
        <div className={cn(
          "absolute inset-0 bg-black/20 opacity-0",
          "group-hover:opacity-100 transition-all duration-300",
          "flex items-end p-4"
        )}>
          <Button 
            onClick={onAddToCart}
            className="w-full bg-primary hover:bg-primary/90 text-black font-semibold"
            disabled={isApexLocked}
          >
            {isApexLocked ? "Unlock Required" : "Add to Cart"}
          </Button>
        </div>
      </div>
      
      {/* Product Info */}
      <div className="p-4 space-y-2">
        <h3 className="font-medium text-foreground leading-tight">
          {name}
        </h3>
        <p className="text-lg font-semibold text-primary">
          ${price}
        </p>
      </div>
    </Card>
  )
}